// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { compareStandings, resolveTieBreakHandicap } from '@/features/leaderboard/tieBreak';
import { groupItems } from '@/features/game-definitions/groups';

/**
 * Turns what the round endpoints already answer into the rows the live board draws.
 *
 * Deliberately does no scoring. Every number here was decided by a service — score points and
 * merits by the scoring engine, gross and net strokes and each hole's par by tour-service — and
 * this module only groups, sums and orders them (CLAUDE.md §12: the UI is a presentation layer).
 * The one judgement it makes is presentation ordering, and even that defers to the shared tie-break
 * comparator so the board cannot disagree with the summary about who is leading.
 *
 * The input shape is the one production actually sends, checked against a live round.state frame
 * and a live scoreboard (2026-09-11), not the one the frontend's RoundStateEvent type declares.
 * They differ: round.state carries NO `holes` array — only `holesCount`, with `par` and `holeIndex`
 * on every hole entry — and the entries use singular keys (`hole`, `grossStroke`, `netStroke`).
 * A board built off the declared type rendered no par and no net score for every round in
 * production.
 */

/** A game running in the round, as the scoreboard reports it. */
export type BoardGame = {
  /** Case-folded, for keying every lookup in this module. */
  id: string;
  /** The id exactly as the service spelled it, for translation lookups keyed by GUID. */
  rawId: string;
  name: string;
};

/** One player's standing in one game: what the game scored them, and what it awarded them. */
export type BoardGameCell = {
  /** The game's own points — Stableford points, skins won. Null when the game reported none. */
  scorePoints: number | null;
  /** Merits the game awarded. Fractional for a shared place, and legitimately negative. */
  merits: number | null;
  /** Place in that game, as the scoreboard reports it. */
  position: number | null;
};

/** The hole being played: the highest anybody has a score for. */
export type BoardCurrentHole = {
  holeNumber: number;
  par: number | null;
  /** Stroke index — the hole's difficulty ranking, `holeIndex` on the wire. */
  strokeIndex: number | null;
};

export type BoardRow = {
  playerId: string;
  name: string;
  /** Standard competition ranking: players who cannot be separated share a position. */
  position: number;
  /** Holes with a recorded gross score. */
  thru: number;
  grossStrokes: number | null;
  netStrokes: number | null;
  /** Gross against the par of the holes actually played, so it reads mid-round. Null until one is. */
  toPar: number | null;
  cellsByGameId: Record<string, BoardGameCell>;
  /** Merits across every game. */
  totalMerits: number;
  /** Strokes on the current hole, or null when this player has not finished it. */
  currentHoleStrokes: number | null;
  imageKey: string | null;
  imageThumbKey: string | null;
  isAi: boolean;
};

export type BoardStandings = {
  /** Only games with something to show, in the app's standard group order. */
  games: BoardGame[];
  rows: BoardRow[];
  currentHole: BoardCurrentHole | null;
};

/** Services answer in camelCase, older payloads and some proxies in PascalCase. */
const pick = <T,>(source: Record<string, unknown> | null | undefined, ...keys: string[]): T | undefined => {
  if (!source) {
    return undefined;
  }
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) {
      return value as T;
    }
  }
  return undefined;
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toRawId = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const toId = (value: unknown): string | null => toRawId(value).toLowerCase() || null;

export type BoardPlayerInput = Record<string, unknown>;
export type BoardScoreInput = Record<string, unknown>;
export type BoardTotalInput = Record<string, unknown>;
export type BoardHoleEntryInput = Record<string, unknown>;

export type BuildBoardStandingsInput = {
  /** /api/rounds/{id}/players — the primary list, because it carries the handicap and avatar keys. */
  players: BoardPlayerInput[];
  /**
   * round.state → players. The same people with the same names, from a second path.
   *
   * The REST lookup answers `{"players": []}` rather than failing when one of its dependencies
   * blips — and on prod, 2026-09-11, it did exactly that while the BFF recovered from an outage,
   * so the board announced "no players in this round" for a round with four. Taking the union
   * means one path dropping out cannot blank the board while the other still has everyone. These
   * entries carry no avatar keys, so a player known only from here shows initials.
   */
  statePlayers?: BoardPlayerInput[];
  /** /api/rounds/{id}/scoreboard → scores: the game's own points per player. */
  scores: BoardScoreInput[];
  /** /api/rounds/{id}/scoreboard → totals: merits per game, and the total. */
  totals: BoardTotalInput[];
  /** round.state → holeEntries: gross, net, par and stroke index per player per hole. */
  holeEntries: BoardHoleEntryInput[];
  /** round.state → holesCount. Only used to bound the current hole; absent is fine. */
  holesCount?: number | null;
  /**
   * Turns a game id into its display name. Injected rather than imported so this module stays
   * pure and testable; the board passes the local i18n lookup, which needs no API call.
   */
  resolveGameName?: (rawGameId: string) => string;
};

/**
 * Every player either source knows about, the REST entry winning where both do. Keyed by the
 * case-folded id, the same way every other lookup in this module is.
 */
const mergePlayers = (primary: BoardPlayerInput[], fallback: BoardPlayerInput[]): BoardPlayerInput[] => {
  const byId = new Map<string, BoardPlayerInput>();
  for (const player of [...primary, ...fallback]) {
    const id = toId(pick(player, 'id', 'Id', 'playerId', 'PlayerId'));
    if (id && !byId.has(id)) {
      byId.set(id, player);
    }
  }
  return [...byId.values()];
};

export const buildBoardStandings = ({
  players,
  statePlayers = [],
  scores,
  totals,
  holeEntries,
  holesCount,
  resolveGameName,
}: BuildBoardStandingsInput): BoardStandings => {
  // ---- the hole being played -------------------------------------------------------------
  // The highest hole anybody has a score for. Par and the stroke index travel on the entries
  // themselves, so the panel needs no course lookup.
  let currentHoleNumber = 0;
  for (const entry of holeEntries) {
    const holeNumber = toNumber(pick(entry, 'hole', 'Hole', 'holeNumber', 'HoleNumber'));
    const gross = toNumber(pick(entry, 'grossStroke', 'GrossStroke', 'grossStrokes', 'GrossStrokes'));
    if (holeNumber === null || holeNumber <= 0 || gross === null) {
      continue;
    }
    if (typeof holesCount === 'number' && holesCount > 0 && holeNumber > holesCount) {
      continue;
    }
    currentHoleNumber = Math.max(currentHoleNumber, holeNumber);
  }

  let currentHole: BoardCurrentHole | null = null;
  if (currentHoleNumber > 0) {
    const onHole = holeEntries.find(
      (entry) => toNumber(pick(entry, 'hole', 'Hole', 'holeNumber', 'HoleNumber')) === currentHoleNumber,
    );
    currentHole = {
      holeNumber: currentHoleNumber,
      par: toNumber(pick(onHole, 'par', 'Par')),
      strokeIndex: toNumber(pick(onHole, 'holeIndex', 'HoleIndex', 'strokeIndex', 'StrokeIndex')),
    };
  }

  // ---- per-player hole tallies ------------------------------------------------------------
  type HoleTally = {
    gross: number;
    net: number;
    parPlayed: number;
    thru: number;
    hasNet: boolean;
    currentHoleStrokes: number | null;
  };
  const tallyByPlayer = new Map<string, HoleTally>();

  for (const entry of holeEntries) {
    const playerId = toId(pick(entry, 'playerId', 'PlayerId'));
    const holeNumber = toNumber(pick(entry, 'hole', 'Hole', 'holeNumber', 'HoleNumber'));
    if (!playerId || holeNumber === null || holeNumber <= 0) {
      continue;
    }

    // Production sends the singular keys; the plural ones are what the frontend type declares.
    const gross = toNumber(pick(entry, 'grossStroke', 'GrossStroke', 'grossStrokes', 'GrossStrokes'));
    const net = toNumber(pick(entry, 'netStroke', 'NetStroke', 'netStrokes', 'NetStrokes'));

    const tally = tallyByPlayer.get(playerId) ?? {
      gross: 0,
      net: 0,
      parPlayed: 0,
      thru: 0,
      hasNet: false,
      currentHoleStrokes: null,
    };

    if (gross !== null) {
      tally.gross += gross;
      tally.thru += 1;
      const par = toNumber(pick(entry, 'par', 'Par'));
      if (par !== null) {
        tally.parPlayed += par;
      }
      if (holeNumber === currentHoleNumber) {
        tally.currentHoleStrokes = gross;
      }
    }
    if (net !== null) {
      tally.net += net;
      tally.hasNet = true;
    }

    tallyByPlayer.set(playerId, tally);
  }

  // ---- games, their points and their merits ------------------------------------------------
  const rawIdByGame = new Map<string, string>();
  const noteGame = (raw: string) => {
    const id = raw.trim().toLowerCase();
    if (id && !rawIdByGame.has(id)) {
      rawIdByGame.set(id, raw.trim());
    }
  };

  const scorePointsByPlayerGame = new Map<string, Map<string, number>>();
  for (const score of scores) {
    const raw = toRawId(pick(score, 'gameId', 'GameId'));
    const playerId = toId(pick(score, 'playerId', 'PlayerId'));
    if (!raw || !playerId) {
      continue;
    }
    noteGame(raw);
    const points = toNumber(pick(score, 'scorePoints', 'ScorePoints'));
    const perGame = scorePointsByPlayerGame.get(playerId) ?? new Map<string, number>();
    if (points !== null) {
      perGame.set(raw.toLowerCase(), points);
    }
    scorePointsByPlayerGame.set(playerId, perGame);
  }

  const meritsByPlayerGame = new Map<string, Map<string, { merits: number | null; position: number | null }>>();
  const totalMeritsByPlayer = new Map<string, number>();
  for (const total of totals) {
    const playerId = toId(pick(total, 'playerId', 'PlayerId'));
    if (!playerId) {
      continue;
    }
    const totalMerits = toNumber(pick(total, 'totalRoundGamePoints', 'TotalRoundGamePoints'));
    if (totalMerits !== null) {
      totalMeritsByPlayer.set(playerId, totalMerits);
    }

    const perGame = meritsByPlayerGame.get(playerId) ?? new Map<string, { merits: number | null; position: number | null }>();
    for (const entry of pick<Record<string, unknown>[]>(total, 'roundGamePoints', 'RoundGamePoints') ?? []) {
      const raw = toRawId(pick(entry, 'gameId', 'GameId'));
      if (!raw) {
        continue;
      }
      noteGame(raw);
      perGame.set(raw.toLowerCase(), {
        merits: toNumber(pick(entry, 'roundGamePoints', 'RoundGamePoints')),
        position: toNumber(pick(entry, 'position', 'Position')),
      });
    }
    meritsByPlayerGame.set(playerId, perGame);
  }

  /**
   * A game where every player sits on nil points and nil merits has nothing to say, and a wall
   * board has no column to spare for it. A round here runs nine games and roughly five are in
   * that state at any moment.
   */
  const hasSomethingToShow = (gameId: string): boolean => {
    for (const perGame of scorePointsByPlayerGame.values()) {
      const points = perGame.get(gameId);
      if (points !== undefined && points !== 0) {
        return true;
      }
    }
    for (const perGame of meritsByPlayerGame.values()) {
      const cell = perGame.get(gameId);
      if (cell && cell.merits !== null && cell.merits !== 0) {
        return true;
      }
    }
    return false;
  };

  const candidates: BoardGame[] = [...rawIdByGame.entries()]
    .filter(([id]) => hasSomethingToShow(id))
    .map(([id, rawId]) => ({ id, rawId, name: resolveGameName ? resolveGameName(rawId) : '' }));

  // The app's standard order — main, match, team, hazard, achievement, challenge, tournament,
  // other. The resolved NAME is passed as well as the id on purpose: several GROUP_BY_GAME_ID
  // entries are stale ids that only match via the name slug, and an id-only call drops those
  // games into "other".
  const games: BoardGame[] = groupItems(
    candidates,
    (game) => game.rawId,
    (game) => game.name,
  ).flatMap((group) => group.items);

  // ---- rows ---------------------------------------------------------------------------------
  const unranked = mergePlayers(players, statePlayers)
    .map((player) => {
      const playerId = toId(pick(player, 'id', 'Id', 'playerId', 'PlayerId'));
      if (!playerId) {
        return null;
      }
      const name = (pick<string>(player, 'name', 'Name') ?? '').trim() || '—';
      const tally = tallyByPlayer.get(playerId);
      const scorePoints = scorePointsByPlayerGame.get(playerId);
      const merits = meritsByPlayerGame.get(playerId);

      const cellsByGameId: Record<string, BoardGameCell> = {};
      for (const game of games) {
        const meritCell = merits?.get(game.id);
        cellsByGameId[game.id] = {
          scorePoints: scorePoints?.get(game.id) ?? null,
          merits: meritCell?.merits ?? null,
          position: meritCell?.position ?? null,
        };
      }

      return {
        playerId,
        name,
        thru: tally?.thru ?? 0,
        grossStrokes: tally && tally.thru > 0 ? tally.gross : null,
        netStrokes: tally && tally.hasNet ? tally.net : null,
        toPar: tally && tally.thru > 0 ? tally.gross - tally.parPlayed : null,
        cellsByGameId,
        totalMerits: totalMeritsByPlayer.get(playerId) ?? 0,
        currentHoleStrokes: tally?.currentHoleStrokes ?? null,
        imageKey: pick<string>(player, 'imageKey', 'ImageKey') ?? null,
        imageThumbKey: pick<string>(player, 'imageThumbKey', 'ImageThumbKey') ?? null,
        isAi: Boolean(pick(player, 'isAi', 'IsAi')),
        handicap: resolveTieBreakHandicap(
          pick(player, 'handicap', 'Handicap'),
          pick(player, 'shcp', 'Shcp', 'scp', 'Scp'),
        ),
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  const ordered = unranked
    .slice()
    .sort((a, b) =>
      compareStandings(
        { merits: a.totalMerits, handicap: a.handicap, name: a.name },
        { merits: b.totalMerits, handicap: b.handicap, name: b.name },
      ),
    );

  // Standard competition ranking: only a genuine tie on merits shares a place. Two players the
  // comparator separated on handicap are ordered but not level, so they get distinct positions.
  const rows: BoardRow[] = [];
  let previousMerits: number | null = null;
  let previousPosition = 0;
  ordered.forEach((row, index) => {
    const position =
      previousMerits !== null && row.totalMerits === previousMerits ? previousPosition : index + 1;
    previousMerits = row.totalMerits;
    previousPosition = position;
    const { handicap: _handicap, ...rest } = row;
    rows.push({ ...rest, position });
  });

  return { games, rows, currentHole };
};
